import { getRouteApi } from "@tanstack/react-router"
import { useStore } from "zustand"
import { createStore, type StoreApi } from "zustand/vanilla"

import type {
  TalentCategory,
  TalentConfigBoss,
  TalentConfigSeason,
  TalentConfigTalent,
} from "@/lib/talent-config-api"
import type {
  NewTalentDraft,
  TalentDraft,
} from "@/lib/utils/talent-draft.utils"

import { BOSS_SEASONS } from "@/core/talent-tracking/seasons.js"

type BossDraft = {
  season: TalentConfigSeason
  name: string
  sortOrder: number
}

type NewBossDraft = {
  season: TalentConfigSeason
  name: string
  sortOrder: number
}

type TalentConfigUiState = {
  // Selection state
  selectedSeason: TalentConfigSeason
  selectedBossId: string | null

  // Draft state (HIGH CHURN - causes rerenders on every keystroke)
  bossDraft: BossDraft | null
  talentDrafts: Record<string, TalentDraft>

  // New item drafts
  newBossDraft: NewBossDraft
  newTalentDrafts: Partial<Record<TalentCategory, NewTalentDraft>>

  // UI state (toggles cause rerenders)
  openTracks: Record<TalentCategory, boolean>
  isHierarchyOpen: boolean

  // Dialog state
  deleteTarget: {
    kind: "boss" | "talent"
    id: string
    name?: string
    category?: TalentCategory
    tier?: number
  } | null

  // Page messages
  pageError: string | null
}

const EMPTY_TALENT_DRAFT: TalentDraft = { description: "", reward: "" }
const EMPTY_NEW_TALENT_DRAFT: NewTalentDraft = { description: "", reward: "" }

export { EMPTY_NEW_TALENT_DRAFT }

const buildBossDraft = (boss: TalentConfigBoss) => ({
  season: boss.season,
  name: boss.name,
  sortOrder: boss.sortOrder,
})

const buildTalentDrafts = (
  talents: TalentConfigTalent[],
): Record<string, TalentDraft> => {
  return Object.fromEntries(
    talents.map((talent) => [
      talent.id,
      {
        description: talent.description,
        reward: talent.reward,
      },
    ]),
  )
}

type TalentConfigStoreActions = {
  setErrorState: (errorMsg: string) => void
}

export type TalentConfigUiStore = StoreApi<
  TalentConfigUiState & TalentConfigStoreActions
>

export const createTalentConfigUiStore = () =>
  createStore<TalentConfigUiState & TalentConfigStoreActions>((set) => ({
    selectedSeason: BOSS_SEASONS[0],
    selectedBossId: null,
    bossDraft: null,
    talentDrafts: {},
    newBossDraft: {
      season: BOSS_SEASONS[0],
      name: "",
      sortOrder: 1,
    },
    newTalentDrafts: {},
    openTracks: {
      offensive: false,
      defensive: false,
      strategic: false,
    },
    isHierarchyOpen: true,
    deleteTarget: null,
    pageError: null,

    // callbacks
    setErrorState: (errorMsg) => set(() => ({ pageError: errorMsg })),
  }))

export const onNewBossAdded = (
  store: TalentConfigUiStore,
  boss: TalentConfigBoss,
) =>
  store.setState({
    selectedBossId: boss.id,
    selectedSeason: boss.season,
    newBossDraft: {
      season: boss.season,
      sortOrder: boss.sortOrder + 1,
      // reset the draft fields to empty for the next addition
      name: "",
    },
  })

// Selection actions
export const setSelectedSeason = (
  store: TalentConfigUiStore,
  season: TalentConfigSeason,
) => store.setState({ selectedSeason: season })

export const setSelectedBossId = (
  store: TalentConfigUiStore,
  bossId: string | null,
) => store.setState({ selectedBossId: bossId })

// Draft actions
export const setBossDraft = (
  store: TalentConfigUiStore,
  draft: BossDraft | null,
) => store.setState({ bossDraft: draft })

export const setTalentDrafts = (
  store: TalentConfigUiStore,
  drafts: Record<string, TalentDraft>,
) => store.setState({ talentDrafts: drafts })

export const resetBossTalentDraft = (store: TalentConfigUiStore) =>
  store.setState({ bossDraft: null, talentDrafts: {} })

export const setBossTalentDrafts = (
  store: TalentConfigUiStore,
  boss: TalentConfigBoss,
) =>
  store.setState({
    bossDraft: buildBossDraft(boss),
    talentDrafts: buildTalentDrafts(boss.talents),
  })

export const setTalentDraft = (
  store: TalentConfigUiStore,
  talentId: string,
  draft: TalentDraft,
) =>
  store.setState((state) => ({
    talentDrafts: { ...state.talentDrafts, [talentId]: draft },
  }))

export const updateTalentDraftField = <K extends keyof TalentDraft>(
  store: TalentConfigUiStore,
  talentId: string,
  key: K,
  value: TalentDraft[K],
) =>
  store.setState((state) => ({
    talentDrafts: {
      ...state.talentDrafts,
      [talentId]: {
        ...EMPTY_TALENT_DRAFT,
        ...state.talentDrafts[talentId],
        [key]: value,
      },
    },
  }))

export const updateBossDraftField = <K extends keyof BossDraft>(
  store: TalentConfigUiStore,
  key: K,
  value: BossDraft[K],
) =>
  store.setState((state) => ({
    bossDraft: state.bossDraft ? { ...state.bossDraft, [key]: value } : null,
  }))

// New boss draft actions
export const setNewBossDraft = (
  store: TalentConfigUiStore,
  draft: NewBossDraft,
) => store.setState({ newBossDraft: draft })

export const updateNewBossDraftField = <K extends keyof NewBossDraft>(
  store: TalentConfigUiStore,
  key: K,
  value: NewBossDraft[K],
) =>
  store.setState((state) => ({
    newBossDraft: { ...state.newBossDraft, [key]: value },
  }))

// New talent draft actions
export const setNewTalentDrafts = (
  store: TalentConfigUiStore,
  drafts: Partial<Record<TalentCategory, NewTalentDraft>>,
) => store.setState({ newTalentDrafts: drafts })

export const setNewTalentDraft = (
  store: TalentConfigUiStore,
  category: TalentCategory,
  draft: NewTalentDraft,
) =>
  store.setState((state) => ({
    newTalentDrafts: { ...state.newTalentDrafts, [category]: draft },
  }))

export const updateNewTalentDraftField = <K extends keyof NewTalentDraft>(
  store: TalentConfigUiStore,
  category: TalentCategory,
  key: K,
  value: NewTalentDraft[K],
) =>
  store.setState((state) => ({
    newTalentDrafts: {
      ...state.newTalentDrafts,
      [category]: {
        ...EMPTY_NEW_TALENT_DRAFT,
        ...state.newTalentDrafts[category],
        [key]: value,
      },
    },
  }))

// UI state actions
export const setOpenTracks = (
  store: TalentConfigUiStore,
  open: Record<TalentCategory, boolean>,
) => store.setState({ openTracks: open })

export const setOpenTrack = (
  store: TalentConfigUiStore,
  category: TalentCategory,
  open: boolean,
) =>
  store.setState((state) => ({
    openTracks: { ...state.openTracks, [category]: open },
  }))

export const setIsHierarchyOpen = (store: TalentConfigUiStore, open: boolean) =>
  store.setState({ isHierarchyOpen: open })

// Dialog actions
export const setDeleteTarget = (
  store: TalentConfigUiStore,
  target: TalentConfigUiState["deleteTarget"],
) => store.setState({ deleteTarget: target })

// Page message actions
export const setPageError = (
  store: TalentConfigUiStore,
  error: string | null,
) => store.setState({ pageError: error })

// Selector hooks for fine-grained subscriptions
export const useSelectedSeason = (store: TalentConfigUiStore) =>
  useStore(store, (s) => s.selectedSeason)

export const useSelectedBossId = (store: TalentConfigUiStore) =>
  useStore(store, (s) => s.selectedBossId)

export const useDeleteTarget = (store: TalentConfigUiStore) =>
  useStore(store, (s) => s.deleteTarget)

// Route-scoped access
const talentConfigRouteApi = getRouteApi("/_authed/_admin/talent-config")

export const useTalentConfigRouteStore = () =>
  talentConfigRouteApi.useRouteContext({
    select: (ctx) => ctx.talentConfigStore,
  })
